import { createApi } from '@reduxjs/toolkit/query/react';
import createBaseQueryWithReauth from '@/services/baseQueryWithReauth';

import { Schedule, ScheduleApi } from '../models';
import { mapScheduleApiToSchedule } from '../adapters';
import { PaginatedRequest, PaginatedResponse } from '@/types';

import { API_FLEET_URL } from '@/utils';

export interface ScheduleFilter {
  driverId?: string;
  costModeId?: string;
}

const schedulesService = createApi({
  reducerPath: 'schedulesService',
  baseQuery: createBaseQueryWithReauth(`${API_FLEET_URL}/schedules`),
  tagTypes: ['Schedule', 'Schedules', 'Drivers', 'Assignments'],
  endpoints: (builder) => ({
    getSchedule: builder.query<Schedule, { scheduleId: string }>({
      query: ({ scheduleId }) => ({
        url: `/${scheduleId}`,
      }),
      transformResponse: (response: ScheduleApi) =>
        mapScheduleApiToSchedule(response),
      providesTags: ['Schedule'],
    }),
    getSchedules: builder.query<
      PaginatedResponse<Schedule>,
      { filters?: ScheduleFilter; paginated: PaginatedRequest }
    >({
      query: ({ filters, paginated }) => ({
        url: '',
        params: {
          ...filters,
          offset: (paginated.page - 1) * paginated.pageSize,
          limit: paginated.pageSize,
        },
      }),
      transformResponse: (response: PaginatedResponse<ScheduleApi>) => ({
        ...response,
        items: response.items.map(mapScheduleApiToSchedule),
      }),
      providesTags: ['Schedules'],
    }),
    createSchedule: builder.mutation<Schedule, Partial<Schedule>>({
      query: (schedule) => ({
        url: '',
        method: 'POST',
        body: {
          ...schedule,
          Mo: schedule.Mo ? 1 : 0,
          Tu: schedule.Tu ? 1 : 0,
          We: schedule.We ? 1 : 0,
          Th: schedule.Th ? 1 : 0,
          Fr: schedule.Fr ? 1 : 0,
          Sa: schedule.Sa ? 1 : 0,
          Su: schedule.Su ? 1 : 0,
        },
      }),
      invalidatesTags: ['Schedules', 'Drivers', 'Assignments'],
    }),
    updateSchedule: builder.mutation<Schedule, Partial<Schedule>>({
      query: (schedule) => ({
        url: `/${schedule.id}`,
        method: 'PUT',
        body: {
          ...schedule,
          Mo: schedule.Mo ? 1 : 0,
          Tu: schedule.Tu ? 1 : 0,
          We: schedule.We ? 1 : 0,
          Th: schedule.Th ? 1 : 0,
          Fr: schedule.Fr ? 1 : 0,
          Sa: schedule.Sa ? 1 : 0,
          Su: schedule.Su ? 1 : 0,
        },
      }),
      invalidatesTags: ['Schedule', 'Schedules', 'Drivers', 'Assignments'],
    }),
    deleteSchedule: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Schedules'],
    }),
  }),
});

export default schedulesService;

export const {
  useGetScheduleQuery,
  useGetSchedulesQuery,
  useCreateScheduleMutation,
  useUpdateScheduleMutation,
  useDeleteScheduleMutation,
} = schedulesService;
